import { useEffect, useState } from 'react';

import { MdCloudDownload } from 'react-icons/md';

import { SubmitHandler, useForm } from 'react-hook-form';

import { useLazyQuery } from '@apollo/client';

import { INITIAL_QUERY_STATE_CONFIG } from 'graphql/apollo/config';

import { Button } from 'ui';
import { ModalProps } from 'ui/models/overlay';
import useToastContext from 'ui/hooks/useToast';
import { useModal } from 'ui/contexts/overlay/Modal';
import { ToastProps } from 'ui/contexts/overlay/Toast';
import { useLoading } from 'ui/contexts/overlay/Loading';

import { Pagination } from 'dashboard/components/table';
import { Dashboard, DashboardMainHeaderForm } from 'dashboard/components/dashboard';

import { Transfers, TransferStatus } from 'transfers/models/transfers';
import { TRANSFERS_QUERY } from 'transfers/graphql/transfersQuerys';

import TransfersTable from 'transfers/components/table/TransfersTable';
import TransfersExportModal from 'transfers/components/modal/TransfersExportModal';
import TransfersFormFilters, {
  FormFilters,
} from 'transfers/components/form/TransfersFormFilters';

import { Transfers as TransfersData } from '__factory__';

import { unMaskFormFields } from 'utils/unmask';

import TransfersCounterItem from 'transfers/components/counter/TransfersCounterItem';

import {
  TransfersCounterList,
  TRANSFERS_COUNTER_QUERY,
} from 'transfers/graphql/transfersCounterQuery';
import { dateWithoutTimezone } from 'utils/form';

type State = {
  transfers: Transfers[];
  hasNextPage: boolean;
  hasPreviousPage: boolean;
};

const LIST_ERROR_TOAST: ToastProps = {
  variant: 'danger',
  title: 'Algo deu errado!',
  text: 'Não foi possível carregar a lista de Transferências',
};

const EXPORT_SUCCESS_TOAST: ToastProps = {
  variant: 'primary',
  title: 'Sucesso!',
  text: 'Sucesso ao baixar Relatório Financeiro',
};

const EXPORT_MODAL: ModalProps = {
  variant: 'primary',
  title: 'Baixar relatório de transferência',
  text: 'Selecione o que você quer baixar:',
};

const DASHBOARD_TITLE = 'Transferências';

export default function ListTransfersPage() {
  const { addToast } = useToastContext();
  const { LoadingOverlay, showLoading, closeLoading } = useLoading();

  const {
    ModalOverlay,
    showConfirm: modalShowConfirm,
    closeConfirm: modalCloseConfirm,
  } = useModal();

  const [state, setState] = useState<State>({
    transfers: [],
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const {
    control,
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<FormFilters>();

  const [TransfersList, { data, loading, error, refetch }] = useLazyQuery(
    TRANSFERS_QUERY,
    INITIAL_QUERY_STATE_CONFIG
  );

  useEffect(() => {
    TransfersList();
  }, [TransfersList]);

  const [
    transfersCounterSelect,
    {
      data: transfersCounter,
      loading: loadingTransfersCounter,
      refetch: refetchTransfersCounter,
    },
  ] = useLazyQuery<TransfersCounterList>(TRANSFERS_COUNTER_QUERY);

  useEffect(() => {
    refetchTransfersCounter && refetchTransfersCounter();

    transfersCounterSelect();
  }, [transfersCounterSelect, refetchTransfersCounter]);

  useEffect(() => {
    if (error) {
      addToast(LIST_ERROR_TOAST);
    }
  }, [addToast, error]);

  const isLoading = loading || loadingTransfersCounter;

  useEffect(() => {
    if (isLoading) {
      return showLoading();
    }

    if (data) {
      setState({
        transfers: data.transfers.entries,
        hasNextPage: !!data.transfers.afterCursor,
        hasPreviousPage: !!data.transfers.beforeCursor,
      });
    }

    /* REFACTOR: FACTORY DATA TO REVIEW APP VISUALIZATION */
    setState({
      transfers: [
        TransfersData(1, TransferStatus.FAILED),
        TransfersData(2, TransferStatus.PENDING),
        TransfersData(3, TransferStatus.ACCOMPLISHED),
      ],
      hasNextPage: !!data?.transfers.afterCursor,
      hasPreviousPage: !!data?.transfers.beforeCursor,
    });

    closeLoading();
  }, [data, isLoading, closeLoading, showLoading]);

  const onClickContinue = () => {
    modalCloseConfirm();
    addToast(EXPORT_SUCCESS_TOAST);
  };

  const handleClickNext = () => {
    refetch &&
      refetch({
        before: null,
        after: data.transfers.afterCursor,
      });
  };

  const handleClickBefore = () => {
    refetch &&
      refetch({
        after: null,
        before: data.transfers.beforeCursor,
      });
  };

  const onSubmit: SubmitHandler<FormFilters> = (transfersInput) => {
    const { transferDate, transferRequestDate } = transfersInput;

    const isoDatesWithoutTimezone = [transferDate, transferRequestDate].map(
      (dates) =>
        dates?.map((date) => dateWithoutTimezone(date).toISOString().split('T')[0])
    );

    Object.assign(transfersInput, {
      ...unMaskFormFields(transfersInput),
      transferDate: isoDatesWithoutTimezone[0],
      transferRequestDate: isoDatesWithoutTimezone[1],
    });

    refetch &&
      refetch({
        filters: Object.fromEntries(
          Object.entries(transfersInput).filter(([, value]) => !!value)
        ),
      });
  };

  return (
    <Dashboard
      dashboardMainHeaderTitle={
        <DashboardMainHeaderForm title={DASHBOARD_TITLE}>
          <Button
            size="sm"
            variant="secondaryGray"
            className="justify-end"
            onClick={modalShowConfirm}
          >
            <MdCloudDownload size={20} /> Relatório de Transferência
          </Button>
        </DashboardMainHeaderForm>
      }
    >
      <TransfersCounterItem transfersCounter={transfersCounter?.transfers} />

      <form onSubmit={handleSubmit(onSubmit)}>
        <TransfersFormFilters
          errors={errors}
          control={control}
          setValue={setValue}
          register={register}
          isLoading={isLoading}
        />
      </form>

      <div className="rounded-lg">
        <TransfersTable transfers={state.transfers} />

        <Pagination
          onNextClick={handleClickNext}
          disableNext={!state.hasNextPage}
          onPreviousClick={handleClickBefore}
          disableBefore={!state.hasPreviousPage}
        />
      </div>

      <TransfersExportModal
        modal={EXPORT_MODAL}
        ModalOverlay={ModalOverlay}
        onClickContinue={onClickContinue}
        onClickCloseModal={modalCloseConfirm}
      />

      <LoadingOverlay />
    </Dashboard>
  );
}
